import { useEffect, useRef, useState } from "react";

interface ReviewWidgetSectionProps {
  className?: string;
}

/**
 * Optimized review widget with lazy loading and deferred script loading
 */
export function ReviewWidgetSection({ className = "" }: ReviewWidgetSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Lazy load using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "200px", threshold: 0.01 }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, []);

  // Load script only when visible
  useEffect(() => {
    if (!isVisible || scriptLoaded) return;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://api.frayze.ca/reputation/assets/review-widget.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[src="https://api.frayze.ca/reputation/assets/review-widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [isVisible, scriptLoaded]);

  return (
    <div ref={containerRef} className={className}>
      {isVisible && (
        <div className="w-full">
          <iframe
            className="lc_reviews_widget"
            src="https://api.frayze.ca/reputation/widgets/review_widget/9A5cW9ju0zoGXbGIDmyW"
            frameBorder="0"
            scrolling="no"
            loading="lazy"
            style={{
              minWidth: "100%",
              width: "100%",
              minHeight: "400px",
              border: "none",
              borderRadius: "12px",
            }}
            title="Customer Reviews"
            aria-label="Customer Reviews Widget"
          />
        </div>
      )}
      {!isVisible && (
        <div className="w-full min-h-[400px] bg-muted/20 animate-pulse rounded-xl" />
      )}
    </div>
  );
}

